import { useState, useEffect } from "react";
import { X, Check, Download, Star, Package, Leaf, Utensils } from "lucide-react";
import type { InventoryItem } from "../types/inventory";
import { Button, IconButton } from "./ui/button";

type EditField = "typeItem" | "categorie" | "";

interface SelectionActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onExport: () => void;
  onBulkEdit: (
    updates: Partial<Pick<InventoryItem, "typeItem" | "categorie" | "inCart">>
  ) => void;
  categories: string[];
}

export function SelectionActionBar({
  selectedCount,
  onClearSelection,
  onExport,
  onBulkEdit,
  categories,
}: SelectionActionBarProps) {
  const [editField, setEditField] = useState<EditField>("");
  const [newValue, setNewValue] = useState("");

  if (selectedCount === 0) return null;

  const handleApplyEdit = () => {
    if (!editField || !newValue) {
      return;
    }

    const updates: any = {};

    if (editField === "typeItem") {
      updates[editField] = newValue;
    } else if (editField === "categorie") {
      updates[editField] = newValue;
    }

    onBulkEdit(updates);

    // Reset
    setEditField("");
    setNewValue("");
  };

  return (
    <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 animate-in slide-in-from-bottom-4 duration-300 max-w-[95vw]">
      <div className="bg-[var(--card)]/95 backdrop-blur-xl border-2 border-[var(--color-steel)]/30 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] px-5 sm:px-6 py-4 sm:py-5">
        {/* Mobile Layout */}
        <div className="flex flex-col gap-4 sm:hidden">
          {/* Count and Clear */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onClearSelection}
                className="p-2 hover:bg-[var(--muted)] rounded-lg transition-colors"
                title="Désélectionner tout"
              >
                <X className="w-4 h-4 text-[var(--muted-foreground)]" />
              </button>
              <div className="bg-[var(--color-steel)]/10 border-2 border-[var(--color-steel)] px-4 py-1.5 rounded-full shadow-lg shadow-[var(--color-steel)]/30 whitespace-nowrap">
                <span className="text-sm font-bold text-[var(--color-steel)]">
                  {selectedCount} article{selectedCount > 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>

          {/* Bulk Edit Controls */}
          <div className="flex gap-2">
            <select
              value={editField}
              onChange={(e) => {
                setEditField(e.target.value as any);
                setNewValue("");
              }}
              className="flex-1 px-3 py-2.5 bg-[var(--muted)] border border-[var(--border)] rounded-lg text-[var(--foreground)] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-steel)] focus:border-[var(--color-steel)]"
            >
              <option value="">Modifier...</option>
              <option value="typeItem">Type</option>
              <option value="categorie">Catégorie</option>
            </select>

            {editField === "typeItem" ? (
              <select
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-[var(--muted)] border border-[var(--border)] rounded-lg text-[var(--foreground)] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-steel)] focus:border-[var(--color-steel)]"
              >
                <option value="">Sélectionner...</option>
                <option value="produit_simple">Produit</option>
                <option value="ingredient">Ingrédient</option>
                <option value="recette">Recette</option>
              </select>
            ) : editField === "categorie" ? (
              <select
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-[var(--muted)] border border-[var(--border)] rounded-lg text-[var(--foreground)] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-steel)] focus:border-[var(--color-steel)]"
              >
                <option value="">Sélectionner...</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            ) : null}

            {editField && (
              <button
                onClick={handleApplyEdit}
                disabled={!newValue}
                className="px-3 py-2.5 bg-[var(--color-steel)] hover:bg-[var(--color-steel-light)] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-all font-medium shadow-lg shadow-[var(--color-steel)]/20"
                title="Appliquer"
              >
                <Check className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2 border-t border-[var(--border)]/50">
            <button
              onClick={() => onBulkEdit({ inCart: true })}
              className="flex-1 px-4 py-2.5 bg-[var(--color-amber)]/20 hover:bg-[var(--color-amber)]/30 text-[var(--color-amber)] rounded-lg transition-all flex items-center justify-center border border-[var(--color-amber)]/40 shadow-sm font-medium whitespace-nowrap"
              title="Ajouter au rappel de commande"
            >
              <Star className="w-4 h-4 fill-current" />
            </button>
            <button
              onClick={onExport}
              className="flex-1 px-4 py-2.5 bg-[var(--muted)] hover:bg-[var(--color-steel)]/10 text-[var(--foreground)] rounded-lg transition-all flex items-center justify-center border border-[var(--border)] shadow-sm font-medium whitespace-nowrap"
              title="Exporter la sélection"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex items-center gap-4">
          {/* Clear Button */}
          <button
            onClick={onClearSelection}
            className="p-2 hover:bg-[var(--muted)] rounded-lg transition-colors"
            title="Désélectionner tout"
          >
            <X className="w-4 h-4 text-[var(--muted-foreground)]" />
          </button>

          {/* Selected Count */}
          <div className="flex items-center pr-4 border-r border-[var(--border)]/50">
            <div className="bg-[var(--color-steel)]/10 border-2 border-[var(--color-steel)] px-4 py-1.5 rounded-full shadow-lg shadow-[var(--color-steel)]/30 whitespace-nowrap">
              <span className="text-sm font-bold text-[var(--color-steel)]">
                {selectedCount} article{selectedCount > 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {/* Bulk Edit Controls */}
          <div className="flex items-center gap-2.5 pr-4 border-r border-[var(--border)]/50">
            <select
              value={editField}
              onChange={(e) => {
                setEditField(e.target.value as any);
                setNewValue("");
              }}
              className="px-3 py-2.5 bg-[var(--muted)] border border-[var(--border)] rounded-lg text-[var(--foreground)] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-steel)] focus:border-[var(--color-steel)] min-w-[130px]"
            >
              <option value="">Modifier...</option>
              <option value="typeItem">Type</option>
              <option value="categorie">Catégorie</option>
            </select>

            {editField === "typeItem" ? (
              <select
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="px-3 py-2.5 bg-[var(--muted)] border border-[var(--border)] rounded-lg text-[var(--foreground)] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-steel)] focus:border-[var(--color-steel)] min-w-[150px]"
              >
                <option value="">Sélectionner...</option>
                <option value="produit_simple">Produit</option>
                <option value="ingredient">Ingrédient</option>
                <option value="recette">Recette</option>
              </select>
            ) : editField === "categorie" ? (
              <select
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="px-3 py-2.5 bg-[var(--muted)] border border-[var(--border)] rounded-lg text-[var(--foreground)] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-steel)] focus:border-[var(--color-steel)] min-w-[150px]"
              >
                <option value="">Sélectionner...</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            ) : null}

            {editField && (
              <button
                onClick={handleApplyEdit}
                disabled={!newValue}
                className="px-3 py-2.5 bg-[var(--color-steel)] hover:bg-[var(--color-steel-light)] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-all font-medium shadow-lg shadow-[var(--color-steel)]/20"
                title="Appliquer"
              >
                <Check className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => onBulkEdit({ inCart: true })}
              className="px-4 py-2.5 bg-[var(--color-amber)]/20 hover:bg-[var(--color-amber)]/30 text-[var(--color-amber)] rounded-lg transition-all border border-[var(--color-amber)]/40 shadow-sm font-medium whitespace-nowrap"
              title="Ajouter au rappel de commande"
            >
              <Star className="w-4 h-4 fill-current" />
            </button>
            <button
              onClick={onExport}
              className="px-4 py-2.5 bg-[var(--muted)] hover:bg-[var(--color-steel)]/10 text-[var(--foreground)] rounded-lg transition-all border border-[var(--border)] shadow-sm font-medium whitespace-nowrap"
              title="Exporter la sélection"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}